import { composeTriggerIntro as composeTriggerIntroI18n } from '@/lib/neoChatI18n';

const API_BASE_URL = (() => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'https://api.neogtm.com';
    }
  }
  return 'https://api.neogtm.com';
})();

function chatbotHeaders(tenantId, userId) {
  const headers = { 'Content-Type': 'application/json' };
  if (tenantId) headers['x-tenant-id'] = tenantId;
  if (userId) headers['x-user-id'] = userId;
  return headers;
}

const TRIGGER_PRIORITY = {
  reply_received: 0,
  fresh_persona: 1,
  fresh_momentum: 2,
};

/** Exact intro copy per product spec (1 vs 2–5 items). */
export function composeTriggerIntro(itemCount, language = null) {
  return composeTriggerIntroI18n(itemCount, language);
}

function stripAccountPrefixFromSentence(accountName, sentence) {
  if (!accountName || !sentence) return sentence;
  const prefix = new RegExp(`^${accountName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*[—–-]\\s*`, 'i');
  return sentence.replace(prefix, '').trim() || sentence;
}

export function normalizeTriggerItem(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const accountName = String(raw.account_name ?? raw.accountName ?? '').trim();
  const triggerType = String(raw.trigger_type ?? raw.triggerType ?? '').trim();
  let sentence = String(raw.sentence ?? raw.summary ?? raw.body ?? '').trim();
  sentence = stripAccountPrefixFromSentence(accountName, sentence);
  const chipLabel = String(
    raw.chip?.label ?? raw.chip_label ?? raw.chipLabel ?? raw.action_label ?? '',
  ).trim();
  const chipPrompt = String(
    raw.chip?.prompt ?? raw.chip_prompt ?? raw.chipPrompt ?? raw.prompt ?? chipLabel,
  ).trim();
  const id = raw.id ?? raw.recommendation_id ?? raw.recommendationId ?? null;
  if (!sentence && !chipLabel) return null;
  return {
    id: id != null ? String(id) : null,
    accountName,
    triggerType,
    sentence,
    chipLabel,
    chipPrompt,
    clicked: false,
  };
}

export function sortTriggerItems(items) {
  return [...items].sort((a, b) => {
    const pa = TRIGGER_PRIORITY[a.triggerType] ?? 99;
    const pb = TRIGGER_PRIORITY[b.triggerType] ?? 99;
    return pa - pb;
  });
}

export function buildTriggerNudgesMessage(items, tenantId, language = null) {
  const normalized = sortTriggerItems(
    (Array.isArray(items) ? items : []).map(normalizeTriggerItem).filter(Boolean),
  ).slice(0, 5);
  if (normalized.length === 0) return null;
  const scopedTenant = tenantId != null ? String(tenantId).toLowerCase() : null;
  return {
    sender: 'bot',
    type: 'trigger_nudges',
    text: composeTriggerIntro(normalized.length, language),
    triggerItems: normalized,
    tenantId: scopedTenant,
    id: `trigger-nudges-${scopedTenant || 'default'}-${Date.now()}`,
  };
}

export async function fetchChatbotTriggers(tenantId, userId, { language } = {}) {
  if (!tenantId || !userId) return null;
  const params = new URLSearchParams({
    tenant_id: tenantId,
    user_id: userId,
  });
  if (language) params.set('language', String(language));
  const res = await fetch(`${API_BASE_URL}/api/chatbot/triggers?${params.toString()}`, {
    credentials: 'include',
    headers: chatbotHeaders(tenantId, userId),
  });
  if (!res.ok) return null;
  return res.json().catch(() => null);
}

export async function fetchChatbotTriggersCountOnly(tenantId, userId, { language } = {}) {
  if (!tenantId || !userId) return { unviewed_count: 0, has_triggers: false };
  const params = new URLSearchParams({
    tenant_id: tenantId,
    user_id: userId,
    count_only: 'true',
  });
  if (language) params.set('language', String(language));
  const res = await fetch(`${API_BASE_URL}/api/chatbot/triggers?${params.toString()}`, {
    credentials: 'include',
    headers: chatbotHeaders(tenantId, userId),
  });
  if (!res.ok) return { unviewed_count: 0, has_triggers: false };
  const data = await res.json().catch(() => ({}));
  return {
    unviewed_count: Number(data?.unviewed_count) || 0,
    has_triggers: Boolean(data?.has_triggers),
  };
}

export function markChatbotTriggersViewed(tenantId, userId) {
  if (!tenantId || !userId) return;
  fetch(`${API_BASE_URL}/api/chatbot/triggers/mark-viewed`, {
    method: 'POST',
    credentials: 'include',
    headers: chatbotHeaders(tenantId, userId),
    body: JSON.stringify({ tenant_id: tenantId, user_id: userId }),
  }).catch(() => {});
}
